// public-frontend/src/utils/logger.ts
/**
 * PUBLIC-FRONTEND LOGGER — mirrors frontend/src/utils/logger.ts and the backend
 * ─────────────────────────────────────────────────────────────────────────────
 * Uses pino in browser mode so the same import/usage patterns work across the
 * whole stack (backend, product frontend, public frontend).
 *
 * Level policy
 * ────────────
 *  Development  (import.meta.env.DEV === true)  → "debug"  (all levels visible)
 *  Production   (import.meta.env.PROD === true) → "info"   (debug suppressed)
 *
 * Override at runtime via localStorage:
 *   localStorage.setItem('LOG_LEVEL', 'debug')   // then reload
 *   localStorage.removeItem('LOG_LEVEL')          // back to default
 *
 * Usage
 * ─────
 *   import { log, createChildLogger } from '@/utils/logger';
 *
 *   // Positional API (matches backend + product frontend shim)
 *   log.debug('RegistrationForm', '🔌 mounting');
 *   log.info ('RegistrationForm', '✅ backend online', { status });
 *   log.warn ('Auth', '⚠️ VITE_GOOGLE_CLIENT_ID not set');
 *   log.error('RegistrationForm', '❌ health check failed', { err: String(e) });
 *
 *   // Child-logger API
 *   const authLog = createChildLogger('Auth');
 *   authLog.info({ email }, 'login attempt');
 *
 * Vite / bundlers resolve "pino" → pino/browser automatically; no extra config.
 */

import pino, { type Logger } from 'pino';

// ── Resolve effective log level ───────────────────────────────
// 1. localStorage override (runtime — dev-tools convenience)
// 2. Build-time env  (Vite: import.meta.env.DEV / .PROD)
// 3. Fallback: 'info'
function resolveLevel(): pino.Level {
  try {
    const override = localStorage.getItem('LOG_LEVEL') as pino.Level | null;
    if (override && ['trace', 'debug', 'info', 'warn', 'error', 'fatal'].includes(override)) {
      return override;
    }
  } catch {
    // SSR or sandboxed environment — ignore
  }

  const isDev =
    typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV === true;

  return isDev ? 'debug' : 'info';
}

const LOG_LEVEL = resolveLevel();

// ── Root browser logger ───────────────────────────────────────
// pino browser mode routes each level to the matching console method
// (console.debug, console.info, console.warn, console.error).
export const logger: Logger = pino({
  level: LOG_LEVEL,
  browser: {
    asObject: false,
    serialize: true,
    write: {
      trace: (o: object) => console.debug('[TRACE]', o),
      debug: (o: object) => console.debug('[DEBUG]', o),
      info: (o: object) => console.info('[INFO] ', o),
      warn: (o: object) => console.warn('[WARN] ', o),
      error: (o: object) => console.error('[ERROR]', o),
      fatal: (o: object) => console.error('[FATAL]', o),
    },
  },
});

// ── Child-logger factory (same signature as backend/frontend) ─
export function createChildLogger(component: string): Logger {
  return logger.child({ component });
}

// ── Child-logger cache (avoids re-creating per call) ─────────
const _childCache = new Map<string, Logger>();
function _child(component: string): Logger {
  let child = _childCache.get(component);
  if (!child) {
    child = logger.child({ component });
    _childCache.set(component, child);
  }
  return child;
}

// ── Type shared with backend/frontend ─────────────────────────
type LogData = Record<string, unknown>;

// ── Positional-API shim (identical signature to backend log.*) ─
export const log = {
  trace: (component: string, msg: string, data?: LogData) =>
    _child(component).trace(data ?? {}, msg),

  debug: (component: string, msg: string, data?: LogData) =>
    _child(component).debug(data ?? {}, msg),

  info: (component: string, msg: string, data?: LogData) =>
    _child(component).info(data ?? {}, msg),

  warn: (component: string, msg: string, data?: LogData) =>
    _child(component).warn(data ?? {}, msg),

  error: (component: string, msg: string, data?: LogData) =>
    _child(component).error(data ?? {}, msg),

  fatal: (component: string, msg: string, data?: LogData) =>
    _child(component).fatal(data ?? {}, msg),
} as const;
